import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Edlink integration service (isolated from CanvasService).
 *
 * Stage 1 runs against an Edlink *sandbox* university — sign-in and
 * course reads work without any real institution. The SSO sign-in
 * itself is a full-page redirect (OAuth2), so it does not go through
 * HttpClient; `startSso()` navigates the browser to the backend start route.
 */

export interface EdlinkStatus {
  connected: boolean;
  edlinkPersonId: string | null;
  edlinkIntegrationId: string | null;
  edlinkUserName: string | null;
  connectedAt: string | null;
}

export interface EdlinkCourse {
  id: string;
  name: string;
  code?: string | null;
  subject?: string | null;
  sessionId?: string | null;
  state?: string | null;
  /** The signed-in user's role in this course (live from enrollments). */
  role?: 'teacher' | 'student' | null;
}

export interface EdlinkPage {
  id: string;
  title: string;
  state?: string | null;
  selected?: boolean;
}

export interface EdlinkFile {
  id: string;
  displayName: string;
  url: string | null;
  thumbnailUrl?: string | null;
  size?: number | null;
  mediaType?: string | null;
  supported?: boolean;
  selected?: boolean;
}

export interface EdlinkImportPayload {
  courseId: string;
  courseName?: string;
  pages?: Array<{ id: string; title: string }>;
  files?: Array<{ id: string; displayName: string; url: string | null; mediaType?: string | null }>;
}

@Injectable({ providedIn: 'root' })
export class EdlinkService {
  private readonly baseUrl = `${environment.baseUrl}/edlink`;

  constructor(private http: HttpClient) {}

  /** Full-page redirect into the "Login with Edlink" OAuth2 flow. */
  get ssoStartUrl(): string {
    return environment.universitySsoUrl || `${this.baseUrl}/sso/login`;
  }

  startSso(): void {
    window.location.href = this.ssoStartUrl;
  }

  getStatus(): Observable<{ success: boolean; data: EdlinkStatus }> {
    return this.http.get<{ success: boolean; data: EdlinkStatus }>(`${this.baseUrl}/status`);
  }

  disconnect(): Observable<any> {
    return this.http.post(`${this.baseUrl}/disconnect`, {});
  }

  listCourses(): Observable<{ success: boolean; data: EdlinkCourse[] }> {
    return this.http.get<{ success: boolean; data: EdlinkCourse[] }>(`${this.baseUrl}/courses`);
  }

  listPages(courseId: string): Observable<{ success: boolean; data: EdlinkPage[] }> {
    return this.http.get<{ success: boolean; data: EdlinkPage[] }>(`${this.baseUrl}/courses/${courseId}/pages`);
  }

  listFiles(courseId: string): Observable<{ success: boolean; data: EdlinkFile[] }> {
    return this.http.get<{ success: boolean; data: EdlinkFile[] }>(`${this.baseUrl}/courses/${courseId}/files`);
  }

  importCourse(payload: EdlinkImportPayload): Observable<any> {
    return this.http.post(`${this.baseUrl}/import`, payload);
  }
}
